import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, Box, styled, TextField } from '@mui/material';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@mui/icons-material';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as clientSlice from '@/data/client.slice';
import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { useRecentPurposeCodeUUIDs } from '../hooks/useRecentPurposeCodeUUIDs';
import {
  buildReasonForTripOptions,
  filterReasonForTripOptions,
  type ReasonForTripOption,
} from '../utils/reasonForTripOptions';
import { usePortalContext } from '@/contexts/PortalProvider';

const PREFIX = 'ReasonForTripField';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function ReasonForTripField({ index }: { index: number }) {
  const { t } = useTranslation('common');
  const { control, trigger } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const { isMemberPortal } = usePortalContext();
  const actor = fieldRefs.reasonForTrip;
  const snapshot = useFieldActorSnapshot(actor);
  const values = (snapshot.context as TripArrayFieldContext).values;
  const raw = values[index];
  const value = typeof raw === 'string' ? raw : '';

  /**
   * Only what the user has typed, never the selected option's label. MUI reuses
   * `inputValue` for both, so filtering off it directly would narrow the list to
   * the current selection as soon as the drop-down is reopened.
   */
  const [searchText, setSearchText] = useState('');

  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );

  const purposeCodes = useMemo(
    () => passengerClientData?.purposeCodes ?? [],
    [passengerClientData?.purposeCodes],
  );

  const recentPurposeCodeUUIDs = useRecentPurposeCodeUUIDs();

  const options = useMemo(
    () =>
      buildReasonForTripOptions(
        purposeCodes,
        recentPurposeCodeUUIDs,
        isMemberPortal,
      ),
    [purposeCodes, recentPurposeCodeUUIDs, isMemberPortal],
  );

  const hasRecent = options.some((option) => option.group === 'recent');

  const selectedOption =
    options.find((option) => option.purposeCodeUUID === value) ?? null;

  const groupLabels: Record<ReasonForTripOption['group'], string> = {
    recent: t('member.reasonForTripRecent'),
    all: t('member.reasonForTripAllReasons'),
  };

  return (
    <StyledBox className={classes.container}>
      <Controller
        name={`trips.${index}.reasonForTrip`}
        control={control}
        render={({ field, fieldState }) => (
          <Autocomplete<ReasonForTripOption, false, false, false>
            options={options}
            value={selectedOption}
            openOnFocus
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            popupIcon={<ExpandMoreRoundedIcon />}
            noOptionsText={t('member.reasonForTripNoMatches')}
            componentsProps={{
              paper: {
                sx: {
                  '& .MuiAutocomplete-groupLabel': {
                    fontWeight: 'bold',
                    color: 'text.primary',
                  },
                },
              },
            }}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, selected) =>
              option.purposeCodeUUID === selected.purposeCodeUUID
            }
            // Headers only make sense once there is something to contrast the
            // full list against.
            groupBy={
              hasRecent ? (option) => groupLabels[option.group] : undefined
            }
            filterOptions={(unfiltered) =>
              filterReasonForTripOptions(unfiltered, searchText)
            }
            onInputChange={(_event, next, reason) =>
              setSearchText(reason === 'input' ? next : '')
            }
            renderOption={(props, option) => (
              <li {...props} key={option.purposeCodeUUID}>
                {option.name}
              </li>
            )}
            onChange={(_event, selected) => {
              const v = selected?.purposeCodeUUID ?? '';
              actor.send({ type: 'CHANGE', index, value: v });
              field.onChange(v);
              // Re-validate just this field so its surfaced required error
              // clears on selection. Under `mode: 'onBlur'` RHF skips
              // change-time validation before submit, so without this the
              // error lingers until the dropdown is blurred. Scoped to the
              // field itself so it doesn't surface errors on other fields or
              // trip legs.
              trigger(`trips.${index}.reasonForTrip`);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name={field.name}
                inputRef={field.ref}
                label={t('member.reasonForTrip')}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                onBlur={() => {
                  actor.send({ type: 'BLUR', index });
                  field.onBlur();
                }}
                onFocus={() => actor.send({ type: 'FOCUS', index })}
              />
            )}
          />
        )}
      />
    </StyledBox>
  );
}
